/**
 * frontend/src/sdk/hooks/use-alpha.ts
 *
 * Typed TanStack Query hooks for the alpha pipeline (radar → investigation → debate).
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api-client";
import type {
  AlphaPipelineRun,
  InvestigationResult,
  RadarSignal,
} from "@/sdk/types/alpha";
import type { AlertLevel } from "@/sdk/types/core";

// =============================================================================
// RADAR / TIER 1
// Backend: GET /api/radar/signals (radar router, prefix /api/radar) — Tier 1
//          viral_signals rows, with SERVER-SIDE alert_level filtering
//          (NOISE/LOW/MEDIUM/HIGH/CRITICAL, case-insensitive). The previously
//          used /api/signals/live ignores alert_level entirely and returns
//          SignalEngine trade-flow items, not radar rows.
// =============================================================================

/** Raw viral_signals row as served by GET /api/radar/signals (ViralSignalRecord). */
export interface RadarViralSignalRow {
  id: number;
  market_id: string;
  timestamp: string;
  gdelt_velocity: number | null;
  gdelt_sigma: number | null;
  trends_velocity: number | null;
  trends_sigma: number | null;
  twitter_velocity: number | null;
  twitter_sigma: number | null;
  composite_sigma: number;
  alert_level: string;
  divergence: number | null;
  implied_price: number | null;
  market_price_at_scan: number | null;
  is_actionable: boolean;
  needs_investigation: boolean;
}

export interface RadarViralSignalsResponse {
  signals: RadarViralSignalRow[];
  returned: number;
  market_id: string | null;
  db_available: boolean;
}

/** The legacy RadarSignal contract plus the radar-native fields the row carries. */
export type RadarSignalEntry = RadarSignal & {
  composite_sigma: number;
  divergence: number | null;
  is_actionable: boolean;
  needs_investigation: boolean;
};

function toRadarSignalEntry(row: RadarViralSignalRow): RadarSignalEntry {
  return {
    market_id: row.market_id,
    // viral_signals rows do not carry the market question — consumers fall
    // back to market_id; never fabricate one.
    question: null,
    // Backend stores lowercase AlertLevel enum values and validates the set,
    // so the lowercased value is one of the five union members.
    alert_level: String(row.alert_level ?? "").toLowerCase() as AlertLevel,
    category: "—",
    // The 0-1 composite velocity is not in the radar row (it carries sigma
    // instead). NaN renders "n/a" via asPercent — never a fabricated 0%.
    velocity_score: Number.NaN,
    velocity_sources: [],
    price_at_detection: row.market_price_at_scan,
    detected_at: row.timestamp,
    composite_sigma: row.composite_sigma,
    divergence: row.divergence,
    is_actionable: row.is_actionable,
    needs_investigation: row.needs_investigation,
  };
}

export function useRadarSignals(options?: { alert_level?: AlertLevel; limit?: number }) {
  const params = new URLSearchParams();
  if (options?.alert_level) params.set("alert_level", options.alert_level);
  if (options?.limit) params.set("limit", String(options.limit));

  return useQuery({
    queryKey: ["alpha", "radar", options],
    queryFn: async () => {
      const response = await apiGet<RadarViralSignalsResponse>(
        `/api/radar/signals?${params}`,
      );
      const rows = response.signals ?? [];
      return {
        signals: rows.map(toRadarSignalEntry),
        total: response.returned ?? rows.length,
        db_available: response.db_available ?? false,
      };
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useViralMarkets(limit = 20) {
  return useQuery({
    queryKey: ["alpha", "viral", limit],
    queryFn: () =>
      apiGet<{ markets: RadarSignal[]; total: number }>(
        `/api/signals/live?limit=${limit}`,
      ),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

// =============================================================================
// INVESTIGATION / TIER 2
// Backend: GET /api/scanner/market/{market_id} — single-market deep analysis.
// =============================================================================

export function useInvestigation(marketId: string, enabled = true) {
  return useQuery({
    queryKey: ["alpha", "investigation", marketId],
    queryFn: () =>
      apiGet<InvestigationResult>(`/api/scanner/market/${encodeURIComponent(marketId)}`),
    enabled: enabled && Boolean(marketId),
    staleTime: 120_000,
  });
}

// =============================================================================
// DEBATE / TIER 3
// Backend: GET /api/debates/recent filtered by market_id (debates router).
//          There is no per-market debate verdict endpoint; fetch recent debates
//          and let the component filter client-side, or use the council ask flow.
// =============================================================================

export function useDebateVerdict(marketId: string, enabled = true) {
  return useQuery({
    queryKey: ["alpha", "debate", marketId],
    // Backend now filters by market_id; falls back to most-recent verdict if not found
    queryFn: () =>
      apiGet<RecentDebatesResponse>(`/api/debates/recent?market_id=${encodeURIComponent(marketId)}&limit=1`),
    enabled: enabled && Boolean(marketId),
    staleTime: 120_000,
  });
}

// =============================================================================
// PIPELINE
// Backend: POST /api/scanner/analyze — full alpha scan entry point.
//          GET  /api/scanner/market/{id} — per-market status.
// =============================================================================

export function useAlphaPipelineStatus() {
  return useQuery({
    queryKey: ["alpha", "pipeline", "status"],
    // POST /api/scanner/analyze is write-only; GET /api/scanner/jobs returns recent runs
    queryFn: () => apiGet<{ jobs: AlphaPipelineRun[] }>("/api/scanner/jobs?limit=1"),
    staleTime: 30_000,
  });
}

export function useRunAlphaScan() {
  return useMutation({
    mutationFn: (params?: { market_ids?: string[]; limit?: number }) =>
      apiPost<AlphaPipelineRun>(
        "/api/scanner/analyze",
        (params ?? {}) as Record<string, unknown>,
      ),
  });
}

// =============================================================================
// CALIBRATION
// Backend: GET /api/calibration/metrics (command_center router) — real
//          CalibrationTracker read. /api/predictions/calibration is a
//          hardcoded empty stub (always bins: [], total_predictions: 0) and
//          must not back a calibration surface.
// =============================================================================

/** Response model of GET /api/calibration/metrics (command_center.CalibrationMetrics). */
export interface CalibrationMetricsResponse {
  brier_score: number;
  log_score: number;
  calibration_error: number;
  total_predictions: number;
  resolved_predictions: number;
  curve: Array<{ predicted: number; actual: number; count?: number }>;
}

export function useCalibrationScore() {
  return useQuery({
    queryKey: ["alpha", "calibration"],
    queryFn: () => apiGet<CalibrationMetricsResponse>("/api/calibration/metrics"),
    staleTime: 300_000,
  });
}

// =============================================================================
// BRIER SCORE DASHBOARD
// =============================================================================

export interface BrierAgentScore {
  name: string;
  brier_score: number | null;
  predictions: number;
  accuracy_rate: number | null;
  quality: string;
  rank?: number;
}

export interface BrierSummary {
  period_days: number;
  overall: {
    brier_score: number | null;
    best_brier: number | null;
    worst_brier: number | null;
    accuracy_rate: number | null;
    total_predictions: number;
    quality: string;
  };
  agents: BrierAgentScore[];
  benchmark: {
    random_guess: number;
    good_threshold: number;
    excellent_threshold: number;
  };
  generated_at: string;
  error?: string;
}

export interface BrierStatusResponse {
  status: string;
  pending_count: number;
  resolved_count: number;
  voided_count: number;
  recent_resolutions: Record<string, unknown>[];
  polling: Record<string, unknown>;
  generated_at: string;
  error?: string;
}

export function useBrierSummary(days = 30) {
  return useQuery({
    queryKey: ["alpha", "brier", "summary", days],
    queryFn: () => apiGet<BrierSummary>(`/api/alpha/brier/summary?days=${days}`),
    staleTime: 300_000,
    refetchInterval: 300_000,
  });
}

export function useBrierStatus() {
  return useQuery({
    queryKey: ["alpha", "brier", "status"],
    queryFn: () => apiGet<BrierStatusResponse>("/api/alpha/brier/status"),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

// =============================================================================
// LEARNED COUNCIL WEIGHTS (resolution feedback loop)
// Backend: GET /api/feedback/weights — the learned_weights table the Tier 3
//          Debate Council reads at runtime. A category's weights only override
//          the static defaults when they carry per-seat keys (quant, skeptic,
//          scout, analyst, contrarian, judge); an aggregate-only entry (e.g.
//          just "system") means the council is still on STATIC weights.
// =============================================================================

/** The six council seat keys learned weights must carry to take effect. */
export const COUNCIL_SEAT_KEYS = [
  "quant",
  "skeptic",
  "scout",
  "analyst",
  "contrarian",
  "judge",
] as const;

export interface LearnedCategoryWeights {
  weights: Record<string, number>;
  sample_size: number;
  avg_brier: number | null;
  last_updated: string;
}

export interface FeedbackWeightsResponse {
  weights: Record<string, LearnedCategoryWeights>;
  models?: Record<string, unknown>[];
  error?: string;
}

/**
 * True when a learned-weights entry actually influences the council — i.e.
 * it carries at least one per-seat key the weighted aggregate can match.
 */
export function learnedWeightsActive(entry: LearnedCategoryWeights): boolean {
  return COUNCIL_SEAT_KEYS.some((seat) => seat in (entry.weights ?? {}));
}

export function useFeedbackWeights() {
  return useQuery({
    queryKey: ["feedback", "weights"],
    queryFn: () => apiGet<FeedbackWeightsResponse>("/api/feedback/weights"),
    staleTime: 300_000,
    refetchInterval: 300_000,
  });
}

// =============================================================================
// MATRIX / NNF DASHBOARD
// =============================================================================

export interface MatrixMarket {
  market_id: string;
  question: string;
  nnf_score: number;
  edge: number;
  direction: "YES" | "NO";
  confidence: number;
  ai_probability: number;
  market_price: number;
  triggers: { pending: number; filled: number };
  category: string;
  alert_level: string;
  last_scanned: string | null;
}

export interface MatrixMarketsResponse {
  markets: MatrixMarket[];
  total: number;
  generated_at: string;
}

export function useMatrixMarkets() {
  return useQuery({
    queryKey: ["matrix", "markets"],
    queryFn: () => apiGet<MatrixMarketsResponse>("/api/matrix/markets"),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

// =============================================================================
// DEBATE COUNCIL VISUALIZATION
// =============================================================================

export interface DebateBlindVote {
  agent_id: string;
  model: string;
  probability: number | null;
  confidence: number | null;
  direction: string;
  reasoning: string;
  latency_ms: number | null;
}

export interface DebateCrossExamVote {
  agent_id: string;
  model: string;
  probability_before: number | null;
  probability_after: number | null;
  probability_delta: number | null;
  confidence_before: number | null;
  confidence_after: number | null;
  confidence_delta: number | null;
  direction: string;
  reasoning: string;
}

export interface DebateCrossExamDelta {
  agent_id: string;
  probability_before: number | null;
  probability_after: number | null;
  probability_delta: number | null;
  did_change: boolean;
}

export interface DebateJudge {
  final_probability: number | null;
  confidence: number | null;
  edge: number | null;
  direction: string | null;
  kelly_fraction: number | null;
  recommended_action: string | null;
  key_risks: string[];
  rationale: string;
}

export interface Debate {
  debate_id: string;
  market_id: string;
  question: string;
  created_at: string;
  models_queried: string[];
  consensus_probability: number | null;
  consensus_confidence: number | null;
  blind_votes: DebateBlindVote[];
  cross_exam_votes: DebateCrossExamVote[];
  cross_exam_deltas: DebateCrossExamDelta[];
  cross_exam_changed_outcome: boolean;
  max_cross_exam_shift: number | null;
  judge: DebateJudge;
  started_at: string | null;
  completed_at: string | null;
}

export interface DebateStats {
  total_debates: number;
  avg_cross_exam_shift: number | null;
  debates_with_cross_exam_change: number;
}

export interface RecentDebatesResponse {
  debates: Debate[];
  total: number;
  limit: number;
  offset: number;
  stats: DebateStats;
  generated_at: string;
  error?: string;
}

export function useRecentDebates(limit = 20) {
  return useQuery({
    queryKey: ["debates", "recent", limit],
    queryFn: () =>
      apiGet<RecentDebatesResponse>(`/api/debates/recent?limit=${limit}`),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

// =============================================================================
// AGENT LEADERBOARD (Debate Council Performance)
// =============================================================================

export interface AgentLeaderboardEntry {
  rank: number;
  agent_id: string;
  model: string;
  total_votes: number;
  brier_score: number | null;
  accuracy: number | null;
  avg_confidence: number | null;
  avg_cross_exam_shift: number | null;
}

export interface AgentLeaderboardResponse {
  leaderboard_30d: AgentLeaderboardEntry[];
  leaderboard_all: AgentLeaderboardEntry[];
  total_debates_30d: number;
  total_debates_all: number;
  generated_at: string;
  error?: string;
}

export function useAgentCouncilLeaderboard() {
  return useQuery({
    queryKey: ["debates", "leaderboard"],
    queryFn: () =>
      apiGet<AgentLeaderboardResponse>("/api/debates/leaderboard"),
    staleTime: 120_000,
    refetchInterval: 300_000,
  });
}
